// utils/priceDisplay.ts
//
// Un solo lugar donde se decide cómo se muestra el precio de un servicio y si
// pasa por el flujo de cobro. Nace del pedido de Adolfo Gallo: atiende por obra
// social y no cobra plus, pero el form no aceptaba precio 0 y terminó cargando
// $1 — que en la landing el paciente lee como un error.
//
// Se centraliza a propósito: `formatPrice` está duplicado en 6 componentes, y la
// lógica duplicada ya nos costó varias rondas de bugs en disponibilidad.

import { PriceMode } from '../services/api';

export const PRICE_MODE_LABELS: Record<Exclude<PriceMode, 'fijo'>, string> = {
  obra_social: 'Obra social',
  consultar: 'A consultar',
};

// Objeto mínimo que necesitamos para decidir: sirve para Service y para los
// servicios que llegan del endpoint público (que traen price como string).
interface PricedService {
  price: number | string;
  price_mode?: PriceMode;
}

const toNumber = (p: number | string): number =>
  typeof p === 'string' ? parseFloat(p) || 0 : p || 0;

/** ¿El paciente paga este servicio por la app? */
export const isChargeable = (s: PricedService): boolean =>
  (s.price_mode ?? 'fijo') === 'fijo';

/** Formato de moneda estándar (es-AR, sin decimales). */
export const formatARS = (price: number | string): string =>
  toNumber(price).toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
  });

/**
 * Texto a mostrar en lugar del importe.
 * Modo 'fijo' → "$ 15.000". Los demás → su etiqueta ("Obra social", "A consultar").
 */
export const displayPrice = (s: PricedService): string => {
  const mode = s.price_mode ?? 'fijo';
  if (mode !== 'fijo') return PRICE_MODE_LABELS[mode];
  return formatARS(s.price);
};

/**
 * Total a cobrar de una selección de servicios: los que no son 'fijo' no suman.
 * Si NINGUNO es cobrable, el turno no debe pasar por MercadoPago ni pedir seña.
 */
export const chargeableTotal = (services: PricedService[]): number =>
  services.reduce((sum, s) => sum + (isChargeable(s) ? toNumber(s.price) : 0), 0);
